import React, { useContext, useEffect, useState } from 'react';
import { ServerInfo } from '@go-shared/types/info';
import { getServerInfo } from '@go-client/lib/api/info';
import { BubbleOverlay } from '@go-client/components/BubbleUI';

export type ServerInfoBundle = ServerInfo & {
  refreshContext: () => Promise<void>;
};

const ServerInfoContext = React.createContext<ServerInfoBundle | undefined>(undefined);

export function useServerInfo() {
  const context = useContext(ServerInfoContext);

  if (context === undefined) {
    throw new Error('useServerInfo must be used within a ServerInfoProvider');
  }

  return context;
}

export function WithServerInfo(props: React.HTMLAttributes<HTMLDivElement>) {
  const { className: propClasses, children, ...rest } = props;

  const [serverInfoBundle, setServerInfoBundle] = useState<ServerInfoBundle | undefined>(undefined);

  // WARNING: If you would like to use state to further complex this refresh function,
  // see UserInfoContext.tsx for an example of how you can do this.
  async function fetchServerInfo() {
    const serverInfo = await getServerInfo();

    setServerInfoBundle({
      ...serverInfo,
      refreshContext: fetchServerInfo,
    });
  }

  useEffect(() => {
    fetchServerInfo().catch(console.error);
  }, []);

  const classes = ['h-full'];
  if (propClasses) classes.push(propClasses);

  return (
    <BubbleOverlay loading={!serverInfoBundle} {...rest} className={classes.join(' ')}>
      {serverInfoBundle && (
        <ServerInfoProvider serverInfoBundle={serverInfoBundle}>
          {props.children}
        </ServerInfoProvider>
      )}
    </BubbleOverlay>
  );
}

interface ServerInfoProviderPropsBase {
  serverInfoBundle: ServerInfoBundle;
}

type ServerInfoProviderProps = ServerInfoProviderPropsBase & { children?: React.ReactNode };

export default function ServerInfoProvider(props: ServerInfoProviderProps) {
  const { serverInfoBundle, children } = props;

  return (
    <ServerInfoContext.Provider value={serverInfoBundle}>{children}</ServerInfoContext.Provider>
  );
}
